import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { dictionaries, milToTime, formatTime } from "../../utils/dictionary";

interface GameTimerProps {
  onTimeoutToggle: () => void;
  language: number;
  variant?: "day" | "night";
  // Timer state for persistence
  accumulated?: number; // Accumulated time when paused (in milliseconds)
  startedAt?: number; // Timestamp when started (undefined if paused)
  onTimerStateChange?: (accumulated: number, startedAt: number | undefined) => void;
}

const TimerContainer = styled.div<{ $variant: "day" | "night" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 18px;
  background: ${(props) =>
    props.$variant === "night"
      ? "rgba(16, 12, 32, 0.75)"
      : "rgba(255, 255, 255, 0.12)"};
  border: 1px solid
    ${(props) =>
      props.$variant === "night"
        ? "rgba(145, 129, 255, 0.3)"
        : "rgba(255, 255, 255, 0.35)"};
  backdrop-filter: blur(8px);
  box-shadow: ${(props) =>
    props.$variant === "night"
      ? "0 16px 40px rgba(15, 10, 31, 0.6)"
      : "0 16px 36px rgba(60, 45, 125, 0.35)"};

  @media (max-width: 768px) {
    padding: 0.4rem 0.6rem;
    border-radius: 12px;
    gap: 0.4rem;
  }
`;

const TimerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-size: 1.1rem;
`;

const TimeDisplay = styled.span`
  font-family: "JetBrains Mono", "SFMono-Regular", "Menlo", monospace;
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #ffffff;
  min-width: 104px;
  text-align: center;
  font-variant-numeric: tabular-nums;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    min-width: 70px;
  }
`;

const TimerButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    gap: 0.4rem;
  }
`;

const TimerButton = styled.button<{ $variant?: "ghost" | "accent"; $hideOnMobile?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-size: 1.15rem;
  color: #ffffff;
  background: ${(props) =>
    props.$variant === "accent"
      ? "linear-gradient(135deg, #5f8cff 0%, #8a6cff 100%)"
      : "rgba(255, 255, 255, 0.16)"};
  box-shadow: ${(props) =>
    props.$variant === "accent"
      ? "0 12px 28px rgba(95, 140, 255, 0.35)"
      : "none"};
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: ${(props) =>
      props.$variant === "accent"
        ? "linear-gradient(135deg, #6f99ff 0%, #9c7dff 100%)"
        : "rgba(255, 255, 255, 0.24)"};
    box-shadow: ${(props) =>
      props.$variant === "accent"
        ? "0 16px 32px rgba(95, 140, 255, 0.45)"
        : "0 10px 22px rgba(0, 0, 0, 0.18)"};
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
    border-radius: 8px;
    display: ${(props) => props.$hideOnMobile ? "none" : "inline-flex"};
  }
`;

const GameTimer: React.FC<GameTimerProps> = ({
  onTimeoutToggle,
  language,
  variant = "day",
  accumulated = 0,
  startedAt,
  onTimerStateChange,
}) => {
  const dict = (dictionaries[language] || dictionaries[0]).ph;
  const [time, setTime] = useState("00:00");
  const [isRunning, setIsRunning] = useState(!!startedAt);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const accumulatedRef = useRef(accumulated);
  const startedAtRef = useRef(startedAt);

  const updateTimeDisplay = useCallback(
    (elapsedMs: number) => {
      const timeObj = milToTime(elapsedMs);
      const formattedTime = formatTime(timeObj);
      setTime(`${formattedTime.m}:${formattedTime.s}`);
    },
    []
  );

  const getCurrentElapsed = useCallback(() => {
    if (startedAtRef.current) {
      const now = Date.now();
      return accumulatedRef.current + (now - startedAtRef.current);
    }
    return accumulatedRef.current;
  }, []);

  const tick = useCallback(() => {
    const currentElapsed = getCurrentElapsed();
    updateTimeDisplay(currentElapsed);
  }, [getCurrentElapsed, updateTimeDisplay]);

  const startTimer = useCallback(() => {
    const now = Date.now();
    startedAtRef.current = now;
    setIsRunning(true);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(tick, 50);
    
    console.log('[GameTimer] START:', {
      accumulated: accumulatedRef.current,
      startedAt: now,
    });
    
    // Notify parent of state change
    if (onTimerStateChange) {
      onTimerStateChange(accumulatedRef.current, now);
    }
  }, [tick, onTimerStateChange]);

  const pauseTimer = useCallback(() => {
    const finalElapsed = getCurrentElapsed();
    accumulatedRef.current = finalElapsed;
    startedAtRef.current = undefined;
    setIsRunning(false);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    updateTimeDisplay(finalElapsed);
    
    console.log('[GameTimer] PAUSE:', {
      accumulated: finalElapsed,
      intervalCleared: !intervalRef.current,
    });
    
    // Notify parent of state change
    if (onTimerStateChange) {
      onTimerStateChange(finalElapsed, undefined);
    }
  }, [getCurrentElapsed, updateTimeDisplay, onTimerStateChange]);

  const resetTimer = useCallback(() => {
    const now = Date.now();
    accumulatedRef.current = 0;
    startedAtRef.current = now;
    setIsRunning(true);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(tick, 50);
    updateTimeDisplay(0);
    
    // Notify parent of state change
    if (onTimerStateChange) {
      onTimerStateChange(0, now);
    }
  }, [tick, updateTimeDisplay, onTimerStateChange]);

  const handleToggle = () => {
    if (isRunning) {
      pauseTimer();
    } else {
      startTimer();
    }
    onTimeoutToggle();
  };

  // Initialize from props only on mount
  useEffect(() => {
    console.log('[GameTimer] MOUNT:', { accumulated, startedAt });
    
    accumulatedRef.current = accumulated;
    startedAtRef.current = startedAt;
    
    // Update display
    updateTimeDisplay(getCurrentElapsed());
    
    // Start interval if timer should be running
    if (startedAt && !intervalRef.current) {
      intervalRef.current = setInterval(tick, 50);
      setIsRunning(true);
      console.log('[GameTimer] Starting interval on mount');
    } else {
      setIsRunning(false);
    }
    
    return () => {
      console.log('[GameTimer] UNMOUNT - before save:', {
        accumulated: accumulatedRef.current,
        startedAt: startedAtRef.current,
        intervalActive: !!intervalRef.current,
      });
      
      // Save state on unmount
      if (onTimerStateChange) {
        // Calculate current elapsed time
        let finalElapsed = accumulatedRef.current;
        let newStartedAt = startedAtRef.current;
        
        if (intervalRef.current && startedAtRef.current) {
          // Timer is running - calculate accumulated and reset startedAt to now
          const now = Date.now();
          finalElapsed = accumulatedRef.current + (now - startedAtRef.current);
          newStartedAt = now;
        } else {
          // Timer is paused
          newStartedAt = undefined;
        }
        
        console.log('[GameTimer] UNMOUNT - saving:', { finalElapsed, newStartedAt });
        onTimerStateChange(finalElapsed, newStartedAt);
      }
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount
  
  // Update display when props change (for external updates)
  useEffect(() => {
    if (!intervalRef.current) {
      updateTimeDisplay(accumulated);
    }
  }, [accumulated, updateTimeDisplay]);

  return (
    <TimerContainer $variant={variant}>
      <TimeDisplay>{time}</TimeDisplay>
      <TimerButtons>
        <TimerButton $hideOnMobile onClick={handleToggle} aria-label={dict.toggle_timer}>
          {isRunning ? "⏸" : "▶"}
        </TimerButton>
        <TimerButton
          $variant="accent"
          onClick={resetTimer}
          aria-label={dict.reset_timer}
        >
          ⟳
        </TimerButton>
      </TimerButtons>
    </TimerContainer>
  );
};

export default GameTimer;
